<script lang="ts">
  import {onMount} from 'svelte';

  import {ethers} from "ethers";

  import MetaMaskOnboarding from '@metamask/onboarding';

  import * as contracts from './lib/contracts';
  import {COMPS} from './lib/contracts';

  import {
    allocation,
    allowance,
    challengeDataNeutral,
    challengeDataUpdown,
    competitionDataNeutral,
    competitionDataUpdown,
    conversionRate,
    deadlineCountdownUpdown,
    getLeaderboard,
    getScoresAndRewards,
    leaderboardNeutral,
    leaderboardUpdown,
    loadContractValues,
    nextDeadlineUpdown,
    scoresAndRewardsNeutral,
    scoresAndRewardsUpdown,
    selection,
    transactions
  } from './stores.js';

  import * as TimeUtil from './lib/timeUtil'

  import Onboarding from './Onboarding.svelte'
  import Notifications from './notifications/Notifications.svelte'
  import FileUploadPopup from './FileUploadPopup.svelte'
  import TransactionsPopup from './TransactionsPopup.svelte'
  import TablesAndCharts from './TablesAndCharts.svelte';
  import RewardsPopup from "./RewardsPopup.svelte";
  // import CompetitionHeader from "./CompetitionHeader.svelte";


  let onboarding = false;

  let stakingPopup        = false;
  let updatingStake       = false;
  let approving           = false;
  let approvingForCompetition = false;
  let converting           = false;
  let predictionPopup     = false;
  let uploadingPrediction = false;
  let showTransactions    = false;
  let rewardsPopup        = false;


  let notifications;

  const preventUnload = (function() {
    const listener = function (evt: Event) {
      // Cancel the event
      evt.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      // Chrome requires returnValue to be set
      // evt.returnValue = '';
    };

    return function (prevent: boolean) {
      if (prevent) {
        window.addEventListener('beforeunload', listener);
      } else {
        window.removeEventListener('beforeunload', listener);
      }
    };
  })();

  const refreshGui = async function() {
    // if ($tournamentData) {
    let gui = false;
    while (!gui) {
      try {
        // TODO: cleanup
        await loadContractValues().then(async () => {
          // TODO check if this is blocking
          // if (!$scoresAndRewardsUpdown) {
            await getScoresAndRewards(COMPS.UPDOWN, $challengeDataUpdown.challenge - 1);
          // }
          // if (!$leaderboardUpdown) {
            await getLeaderboard(COMPS.UPDOWN);
          // }
          // if (!$scoresAndRewardsNeutral) {
            await getScoresAndRewards(COMPS.NEUTRAL, $challengeDataNeutral.challenge - 1);
          // }
          // if (!$leaderboardNeutral) {
            await getLeaderboard(COMPS.NEUTRAL);
          // }
        });

        gui = true;
      } catch (error) {
        console.error('Error with gui setup - retrying');
        console.error(error);
      }

      if (!gui) {
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
      }
    }
    // }
  }

  async function setup() {
    if (!MetaMaskOnboarding.isMetaMaskInstalled()) {
      onboarding = true;
      return;
    }

    try {
      contracts.init();
    } catch (err) {
      // Metamask not installed - this should never happen but left it to be on the safe side
      console.error(err);
      onboarding = true;
      return;
    }

    try {
      if (!(await contracts.isConnected())) {
        // Not on Polygon network
        onboarding = true;
        return;
      }
    } catch (err)  {
      console.error(err);
      onboarding = true;
      return;
    }

    contracts.RciEvents.addListener('accountChanged', refreshGui);

    let setup = false;
    while (!setup) {
      try {
        await contracts.setup(); // TODO: call refreshGui here?
        setup = true;
      } catch (error) {
        console.error('Error with setup - retrying');
        console.error(error)
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
      }
    }

    let connected = false;
    while (!connected) {
      try {
        await contracts.connect();
        connected = true;
      } catch (error) {
        console.error('Error with connect - retrying');
        console.error(error);
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
      }
    }

    setInterval(refreshGui, 10e3);
    onboarding = false;
    // TODO needed to show the user's ranking in the competition
    // console.log(await contracts.getInformation(6)); // TODO
  }

  onMount(setup);

  function showStakingPopup(show: boolean) {
    stakingPopup = show;
  }

  function showRewardsPopup(show: boolean) {
    rewardsPopup = show;
  }

  async function approval() {
    preventUnload(true);
    showRewardsPopup(false);

    let promise: Promise<any>;
    promise = contracts.approve()
    approving = true

    return promise.then(async ([tx, receiptPromise]) => {

      notifications.add('Approving', 'It might take several minutes for the blockchain transaction to complete');
      transactions.update(t => {
        t[tx.hash] = {};
        t[tx.hash].description = `Approve`;
        return t;
      });
      const receipt = await(receiptPromise);

      transactions.update(t => {
        t[tx.hash].new       = true;
        t[tx.hash].from      = receipt.from;
        t[tx.hash].to        = receipt.to;
        t[tx.hash].timestamp = Date.now();
        return t;
      });
      if (receipt.status) {
        notifications.add('Allowance updated, you can now proceed to convert your YIEDL to USDC.')
      } else {
        notifications.add('Error updating allowance.', 'Please try again', 'error')
      }
      refreshGui(); // TODO - only refresh stake
    }).catch(err => {
      console.error(err);
      if (err.code === 'TIMEOUT') {
        notifications.add(`Operation timed out. Please refresh the page and check that your allowance has been updated. If not, please try again.`, null, 'error');
      } else {
        let errorMessage = err.data && err.data.message || 'Can\'t update allowance. Please retry later or contact RCI';
        notifications.add(errorMessage, null, 'error');
        console.log(err.data)
        console.log(err.data.message)
      }
    }).finally(() => {
      preventUnload(false);
      approving = false;
    })
  }

  async function convert(event: CustomEvent) {
    preventUnload(true);
    showRewardsPopup(false);
    const yiedlToConvert = event.detail.yiedlToConvert;

    let promise: Promise<any>;
    promise = contracts.convert(yiedlToConvert)
    converting = true

    return promise.then(async ([tx, receiptPromise]) => {

      notifications.add('Converting YIEDL to USDC.', 'It might take several minutes for the blockchain transaction to complete');
      transactions.update(t => {
        t[tx.hash] = {};
        t[tx.hash].description = `Convert ${ethers.utils.formatUnits(yiedlToConvert, 6)} YIEDL to USDC`;
        return t;
      });
      const receipt = await(receiptPromise);

      console.log("receipt", receipt)

      const amountUsdcHex = receipt.logs[3].topics[2]
      const amountUsdc = ethers.BigNumber.from(amountUsdcHex)
      const amountUsdcFormatted = ethers.utils.formatUnits(amountUsdc, 6)

      transactions.update(t => {
        t[tx.hash].new       = true;
        t[tx.hash].from      = receipt.from;
        t[tx.hash].to        = receipt.to;
        t[tx.hash].timestamp = Date.now();
        return t;
      });
      if (receipt.status) {
        notifications.add(`Conversion successful.\nReceived ${amountUsdcFormatted} USDC.\nPlease check updated USDC in your wallet.`)
      } else {
        notifications.add('Error converting YIEDL to USDC.', 'Please try again', 'error')
      }
      refreshGui();
    }).catch(err => {
      console.error(err);
      if (err.code === 'TIMEOUT') {
        notifications.add(`Operation timed out. Please refresh the page and check that your USDC balance has been updated. If not, please try again.`, null, 'error');
      } else {
        let errorMessage = err.data && err.data.message || 'Can\'t convert YIEDL to USDC. Please retry later or contact RCI';
        notifications.add(errorMessage, null, 'error');
      }
    }).finally(() => {
      preventUnload(false);
      converting = false;
    })
  }

  async function updateStake(event: CustomEvent) {
    preventUnload(true);

    showStakingPopup(false);

    const stake = event.detail.stake;

    let promise: Promise<any>; // TODO: Promise.transactionReceipt or a wrapper
    promise = contracts.setStake($selection, stake);

    updatingStake = true;

    return promise.then(async ([tx, receiptPromise]) => {
      // console.log(tx);
      // TODO move this to stores.js
      notifications.add('Updating your stake', 'It might take several minutes for the blockchain transaction to complete');
      transactions.update(t => {
        t[tx.hash] = {};
        t[tx.hash].description = `Set stake to ${ethers.utils.formatUnits(stake, 6)}`;
        return t;
      });
      const receipt = await(receiptPromise);

      transactions.update(t => {
        t[tx.hash].new       = true;
        t[tx.hash].from      = receipt.from;
        t[tx.hash].to        = receipt.to;
        t[tx.hash].timestamp = Date.now();
        return t;
      });

      if (receipt.status) {
        notifications.add('Your stake has been updated');
      } else {
        notifications.add('Error updating your stake', 'Please try again', 'error')
      }
      refreshGui(); // TODO - only refresh stake
    })
      // .catch(err => {
    //   console.error(err);
    //   if (err.code === 'TIMEOUT') {
    //     notifications.add(`Operation timed out. Please refresh the page and check that the stake has been updated to ${ethers.utils.formatUnits(stake, 6)}. If not, please try again.`, null, 'error');
    //   } else {
    //     let errorMessage = err.data && err.data.message || 'Can\'t update your stake. Please retry later or contact RCI';
    //     notifications.add(errorMessage, null, 'error');
    //   }
    // })
      .finally(() => {
      preventUnload(false);
      updatingStake = false;
    })
  }

  function showUploadPopup(show: boolean) {
    predictionPopup = show;
  }

  async function approvalForCompetition() {
    preventUnload(true);
    showUploadPopup(false);

    let promise: Promise<any>;
    promise = contracts.approveCompetition($selection)
    approvingForCompetition = true

    return promise.then(async ([tx, receiptPromise]) => {

      notifications.add(`Approving ${$selection == COMPS.UPDOWN ? "Updown" : "Neutral"}`, 'It might take several minutes for the blockchain transaction to complete');
      showUploadPopup(false);
      transactions.update(t => {
        t[tx.hash] = {};
        t[tx.hash].description = `Approve`;
        return t;
      });
      const receipt = await(receiptPromise);

      transactions.update(t => {
        t[tx.hash].new       = true;
        t[tx.hash].from      = receipt.from;
        t[tx.hash].to        = receipt.to;
        t[tx.hash].timestamp = Date.now();
        return t;
      });
      if (receipt.status) {
        notifications.add('Allowance updated, you can now proceed to set your stake.')
      } else {
        notifications.add('Error updating allowance.', 'Please try again', 'error')
      }
      refreshGui(); // TODO - only refresh stake
    })
            .catch(err => {
      console.error(err);
      if (err.code === 'TIMEOUT') {
        notifications.add(`Operation timed out. Please refresh the page and check that your allowance has been updated. If not, please try again.`, null, 'error');
      } else {
        let errorMessage = err.data && err.data.message || 'Approval cancelled. Please try again or contact RCI for further help.';
        notifications.add(errorMessage, null, 'error');
        console.log(err.data)
        console.log(err.data.message)
      }
    }).finally(() => {
      preventUnload(false);
      approvingForCompetition = false;
    })
  }

  async function _uploadSubmission(updateFn: (comp: COMPS, amount: ethers.BigNumber, a: File, b: string) => Promise<any[]>, { detail: { predictionFile, stake } }) {
    preventUnload(true);
    showUploadPopup(false);

    const prevHash = ($selection === COMPS.UPDOWN) ? $challengeDataUpdown.submissionHash : $challengeDataNeutral.submissionHash;
    uploadingPrediction = true;
    if (predictionFile) {
      notifications.add(prevHash ? 'Updating your prediction' : 'Uploading your prediction', 'It might take several minutes for the upload and the blockchain transaction to complete');
    }

    const competitionKey = ($selection === COMPS.UPDOWN) ?
            await contracts.retrieve($competitionDataUpdown.competitionKeyHash) :
            await contracts.retrieve($competitionDataNeutral.competitionKeyHash);

    const promise = updateFn($selection, stake, predictionFile, await competitionKey.text());

    return promise.then(async([_, receiptPromise]) => {
      if (predictionFile) {
        notifications.add('File upload complete', 'Now recording on the blockchain')
      }
      // TODO store in log
      const receipt = await receiptPromise;
      if (receipt.status) {
        notifications.add('Stake/Submission update successful');
      } else {
        notifications.add('Error submitting', 'Please update your stake/submission again', 'error')
      }
      refreshGui();
    }).catch(err => {
      console.error(err)
      if (err.code === 'TIMEOUT') {
        notifications.add(`Operation timed out. Please update your stake/submission again`, null, 'error');
      } else {
        // TODO check if it covers all cases
        const errorText = err.message || err.data && err.data.message || err;
        notifications.add(`Error uplodaing your prediction: ${errorText}`, 'Please make your submission again', 'error');
      }
    }).finally(() => {
      uploadingPrediction = false;
      preventUnload(false);
    });
  }

  const uploadSubmission   = (evt: CustomEvent) => _uploadSubmission(contracts.stakeAndSubmit, evt);
  const reUploadSubmission = (evt: CustomEvent) => _uploadSubmission(contracts.stakeAndSubmit, evt);

  function downloadOwnSubmission() {
    if ($selection === COMPS.UPDOWN) {
      contracts.downloadOwnSubmission(COMPS.UPDOWN, $challengeDataUpdown.challenge);
    } else {
      contracts.downloadOwnSubmission(COMPS.NEUTRAL, $challengeDataNeutral.challenge);
    }
  }

  // function showTransactionsPopup() {
  //   showTransactions = true;
  //   const newTransactions = {};
  //   for (const [key, value] of Object.entries($transactions)) {
  //     newTransactions[key] = value;
  //     newTransactions[key].new = false;
  //   }
  //   transactions.set(newTransactions);
  // }



</script>
<!--<CompetitionHeader />-->
{#if onboarding}
  <div class="bg-indigo-700">
    <div class="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
      <h2 class="text-3xl font-extrabold text-white sm:text-4xl">
        <span class="block">Welcome to the YIEDL competition platform.</span>
        <span class="block">Start competing today.</span>
      </h2>

      <p class="mt-4 text-lg leading-6 text-red-100">To participate to the competition you first need to:</p>
      <Onboarding on:switch={setup} />
      <p class="mt-4 text-lg leading-6 text-red-100">After connecting to Polygon the YIEDL Competition dApp should start automatically. If not, please reload this page.
        <!-- <a target="_blank" href="https://polygonscan.com/" class="underline">switch to the Polygon network</a> and reload this page. -->
      </p>
    </div>
  </div>
{/if}

<!-- <div class:hidden={!!$competitionData || installWarning}>
  Loading dapp...
</div> -->

{#if (!$competitionDataUpdown && !$competitionDataNeutral && !onboarding)}
  <div class="bg-white" class:hidden__={!!$competitionDataUpdown || !!$competitionDataNeutral || onboarding}>
    <div class="max-w-5xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
      <h2 class="text-3xl font-bold tracking-tight text-blue-800 sm:text-4xl">
        <span class="block">Please wait while the dApp is loading...</span>
        <!-- <span class="block">Start your free trial today.</span> -->
      </h2>
      <div class="mt-4 w-36 flex justify-center mx-auto text-center text-blue-500">
        <!-- <svg class="animate-spin -ml-1 mr-3 h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
          viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
          <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
        </svg> -->

        <svg version="1.1" id="L9" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
             viewBox="0 0 100 100" enable-background="new 0 0 0 0" xml:space="preserve">
          <path fill="currentColor" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
            <animateTransform
                    attributeName="transform"
                    attributeType="XML"
                    type="rotate"
                    dur="1s"
                    from="0 50 50"
                    to="360 50 50"
                    repeatCount="indefinite" />
          </path>
        </svg>
      </div>
    </div>
  </div>
{/if}

{#if $competitionDataUpdown && $competitionDataNeutral}
  <div class:hidden={!$competitionDataUpdown && !$competitionDataNeutral} class="min-h-screen bg-gray-100">
    <!-- <AllowancePopup allowance={$allowance} on:submit={updateAllowance} on:cancel={() => showAllowancePopup(false)} show={allowanceEditing}></AllowancePopup> -->
<!--    <StakingPopup stake={($selection === COMPS.UPDOWN) ? $challengeDataUpdown.stake : $challengeDataNeutral.stake}-->
<!--                  on:submit={updateStake}-->
<!--                  on:cancel={() => showStakingPopup(false)}-->
<!--                  show={stakingPopup}></StakingPopup>-->
    <RewardsPopup
            on:approve={approval}
            on:submit={convert}
            on:cancel={() => showRewardsPopup(false)}
            show={rewardsPopup}
            allocation={$allocation}
            allowance={$allowance}
            rate={$conversionRate}
    >
  </RewardsPopup>


    <FileUploadPopup
            stake={($selection === COMPS.UPDOWN) ? $challengeDataUpdown.stake : $challengeDataNeutral.stake}
            allowance={(($selection === COMPS.UPDOWN) ? $competitionDataUpdown.allowance : $competitionDataNeutral.allowance)}
            submissionHash={($selection === COMPS.UPDOWN) ? $challengeDataUpdown.submissionHash : $challengeDataNeutral.submissionHash}
            on:approve={approvalForCompetition}
            on:submit={
    ($selection === COMPS.UPDOWN) ?
    (!$challengeDataUpdown.submissionHash ? uploadSubmission : reUploadSubmission)
    :
    (!$challengeDataNeutral.submissionHash ? uploadSubmission : reUploadSubmission)
    }
                     on:cancel={() => showUploadPopup(false)}
                     show={!!predictionPopup} />
    <TransactionsPopup show={showTransactions}></TransactionsPopup>

    <header class="pb-24">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-5xl__ lg:px-8__">
        <div class="h-20 relative flex flex-wrap items-center justify-between">
          <!-- Logo -->
          <div class="left-0 py-5 flex-shrink-0 static">
            <span class="sr-only">Rocket Capital Investment</span>
            <a href="https://www.rocketcapital.ai" target="_blank">
              <svg xmlns="http://www.w3.org/2000/svg" width="41.278" height="30.958" viewBox="0 0 41.278 30.958"><rect width="14.594" height="14.594" transform="translate(0 20.639) rotate(-45)" fill="#45adf7"/><rect width="14.594" height="14.594" transform="translate(10.319 10.319) rotate(-45)" fill="#0328d2"/><rect width="14.594" height="14.594" transform="translate(20.64 20.639) rotate(-45)" fill="#6428ee"/></svg>
            </a>
          </div>
          <div class="ml-4 flex items-center pr-0.5">
            <div class="text-sm font-medium text-center">
              <button on:click={() => contracts.downloadPhaseDataset(COMPS.UPDOWN, $challengeDataUpdown.challenge)} type="button" class="inline-flex items-center px-2 py-1 border border-indigo-700 hover:border-indigo-400 text-sm font-medium rounded-md shadow-sm text-white bg-indigo-700 hover:bg-indigo-500 focus:outline-none">
                Download Dataset <!-- &amp; switch network -->
              </button>

<!--              <button type="button" class="inline-flex items-center px-2 py-1 border border-indigo-300 hover:border-indigo-400 text-sm font-medium rounded-md shadow-sm text-black bg-indigo-200 hover:bg-indigo-300 focus:outline-none">-->
<!--                <a href="https://faucet.polygon.technology/" target="_blank" rel="noopener noreferrer">-->
<!--                  Get Test Matic </a>-->
<!--              </button>-->

<!--              <button on:click={contracts.drip} type="button" class="inline-flex items-center px-2 py-1 border border-red-300 hover:border-red-400 text-sm font-medium rounded-md shadow-sm text-black bg-red-200 hover:bg-red-300 focus:outline-none">-->
<!--                Get 1000 YIEDL (internal)-->
<!--              </button>-->

              <button on:click={contracts.addToken} type="button" class="inline-flex items-center px-2 py-1 border border-indigo-300 hover:border-indigo-400 text-sm font-medium rounded-md shadow-sm text-black bg-indigo-200 hover:bg-indigo-300 focus:outline-none">
                Import {'__TOKEN_SYMBOL__'} <!-- &amp; switch network -->
              </button>

              <button disabled class="inline-flex items-center px-2 py-1 border border-indigo-300 text-sm font-medium rounded-md shadow-sm text-black bg-gray-200" style="cursor: default;">
                {contracts.activeUser.slice(0, 6)}...{contracts.activeUser.slice(-4)}
              </button>

            </div>
          </div>
        </div>
      </div>
    </header>
    <main class="-mt-24 pb-8">

      {#if ethers.utils.parseUnits($allocation, 6).gt(ethers.constants.Zero)}

        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-5xl__ lg:px-8__ mt-6">
          <section aria-labelledby="profile-overview-title">
            <div class="rounded-md bg-indigo-800 overflow-hidden shadow">
              <div class="p-4 border-8 border-purple-600">
                <div class="sm:flex sm:items-center justify-center">
                  <div class="text-2xl text-center font-semibold text-white sm:text-2xl">
                    <span class="tracking-tight">Rewards Conversion</span>
                  </div>
                </div>
                <div class="sm:flex sm:items-center justify-between mt-2">
                  <div class="tracking-tight text-xl text-left font-semibold text-white sm:text-xl">
                    My Rewards Allocation: <span class="tracking-normal">{$allocation}</span>
                    <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>
                  </div>

                  <!--              <div class="hidden sm:block sm:flex-grow"></div>-->
                  <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">
                    <!--                   class:sm:mr-4={$challengeDataNeutral.phase === 1}-->
                    <!--                   class:sm:w-44={!$challengeDataNeutral.submissionHash || $challengeDataNeutral.phase > 1}-->

                    <button on:click={showRewardsPopup} type="button"
                            class="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-md bg-gray-100 text-blue-700 hover:text-blue-800 focus:outline-none"> <!-- focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 -->
                      <!-- Heroicon name: solid/mail -->
                      {#if converting || approving}
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                             viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
                        <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
                      </svg>
                      {:else}

                        <svg class="-ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                          <path d="M32 96l320 0V32c0-12.9 7.8-24.6 19.8-29.6s25.7-2.2 34.9 6.9l96 96c6 6 9.4 14.1 9.4 22.6s-3.4 16.6-9.4 22.6l-96 96c-9.2 9.2-22.9 11.9-34.9 6.9s-19.8-16.6-19.8-29.6V160L32 160c-17.7 0-32-14.3-32-32s14.3-32 32-32zM480 352c17.7 0 32 14.3 32 32s-14.3 32-32 32H160v64c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-96-96c-6-6-9.4-14.1-9.4-22.6s3.4-16.6 9.4-22.6l96-96c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6l0 64H480z"/>
                        </svg>
                      {/if}
                      Convert
                    </button>
                  </div>
                </div>
                <div class="sm:flex sm:items-center justify-between mt-2">
                  <div class="w-full text-white text-base sm:text-md font-medium">
                    <p>Conversion rate: 1 YIEDL = {$conversionRate} USDC</p>
                  </div>
                  <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">
                    <button on:click={contracts.addUsdc} type="button"
                            class="w-full flex items-center justify-center px-2 py-1 border border-indigo-300
                text-xs font-medium rounded-md shadow-sm text-black bg-indigo-200
                focus:outline-none"
                    >
                      <!--              hover:border-indigo-400-->
                      <!--              hover:bg-indigo-300-->
                      Import USDC
                    </button>
                  </div>
                </div>

              </div>
            </div>

          </section>
        </div>

      {/if}

      <div class="w-full text-lg text-center mt-4 justify-center items-center mx-auto max-w-6xl py-6 px-4 mb-4 sm:px-6 lg:px-8 lg:py-1">
        <button class={`${$selection===COMPS.UPDOWN? "bg-indigo-700 text-gray-100 border-indigo-600 border-4" : "bg-gray-300 text-gray-600 border border-white hover:bg-gray-400" }
                      font-bold py-2 px-4 rounded-md`}
                on:click={()=> {selection.set(COMPS.UPDOWN)}}>
          UPDOWN
        </button>
        <button class={`${$selection===COMPS.NEUTRAL? "bg-indigo-700 text-gray-100 border-indigo-600 border-4" : "bg-gray-300 text-gray-600 border border-white hover:bg-gray-400" }
                      font-bold py-2 px-4 rounded-md`}
                on:click={()=> {selection.set(COMPS.NEUTRAL)}}>
          NEUTRAL
        </button>
      </div>


      {#if $selection === COMPS.UPDOWN}
        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-5xl__ lg:px-8__">
          <h1 class="sr-only">Competition</h1>

          <!-- Main 3 column grid -->
          <div class="grid grid-cols-1 gap-4_ items-start lg:grid-cols-3_ lg:gap-8_">

            <!-- Left column -->
            <div class="grid grid-cols-1 gap-4 lg:col-span-2_">
              <!-- Welcome panel -->
              <section aria-labelledby="profile-overview-title">
                <div class="rounded-md bg-white overflow-hidden shadow">
                  <h2 class="sr-only" id="profile-overview-title">Competition Overview</h2>
                  <div class="bg-white p-6">
                    <p class="py-2 text-2xl font-extrabold sm:text-3xl text-center text-purple-900">{contracts.getCompetitionName($selection)} Competition</p>
                  </div>
                  <dl class="grid grid-cols-1 bg-white overflow-hidden border-t border-gray-200 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-y-0 sm:divide-x sm:divide-transparent">
                    <div class="px-3 py-2 sm:py-4">
                      <dt class="text-base font-normal text-center text-gray-900">
                        Total submissions
                      </dt>
                      <dd class="mt-1 justify-between text-2xl font-semibold text-indigo-600 text-center">
                        {$competitionDataUpdown.submissionCounter}
                      </dd>
                    </div>
                    <div class="px-3 py-2 sm:py-4">
                      <dt class="text-base font-normal text-center text-gray-900">
                        Total stake
                      </dt>
                      <dd class="mt-1 justify-between text-2xl font-semibold text-indigo-600 text-center">
                        {$competitionDataUpdown.totalStake}
                        <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>
                      </dd>
                    </div>
<!--                    <div class="px-3 py-2 sm:py-4">-->
<!--                      <dt class="text-base font-normal text-center text-gray-900">-->
<!--                        Pool-->
<!--                      </dt>-->
<!--                      <dd class="mt-1 justify-between text-2xl font-semibold text-indigo-600 text-center">-->
<!--                        {$competitionDataUpdown.competitionPool}-->
<!--                        <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>-->
<!--                      </dd>-->
<!--                    </div>-->
                  </dl>
                </div>
              </section>

              <section aria-labelledby="profile-overview-title">
                <div class="rounded-md bg-blue-800 overflow-hidden shadow">
                  <h2 class="sr-only" id="profile-overview-title">Competition Overview</h2>
                  <!-- <div class="border-b border-indigo-300 bg-indigo-700 grid grid-cols-1 divide-y divide-pink-300 sm:divide-y-0 sm:divide-x">
                    <Steps phase={$challengeData.phase} stakeLocked={!!$challengeData.submissionHash}></Steps>
                  </div> -->
                  <div class="p-8">

                    <div class="sm:flex sm:items-center justify-center">
                      <div class="text-2xl text-center font-semibold text-white sm:text-3xl">
                        <span class="tracking-tight">Challenge:</span> {$challengeDataUpdown.challenge}
                      </div>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700 mx-0 px-0">

                    <div class="sm:flex sm:items-center justify-between mt-6">
                      <div class="tracking-tight text-2xl text-center font-semibold text-white sm:text-3xl">
                        My stake: <span class="tracking-normal">{$challengeDataUpdown.stake.toFixed(2)}</span>
                        <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>
                      </div>

                      {#if $challengeDataUpdown.phase === 1}
<!--                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">-->
<!--                          <button on:click={() => !(updatingStake || uploadingPrediction) && showStakingPopup(true)}-->
<!--                                  class:cursor-default={updatingStake || uploadingPrediction}-->
<!--                                  class:hover:bg-blue-600={!updatingStake || uploadingPrediction}-->
<!--                                  class="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-500 focus:outline-none">-->
<!--                            {#if updatingStake}-->
<!--                              <svg class="animate-spin -ml-1 mr-3 h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"-->
<!--                                   viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">-->
<!--                              <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />-->
<!--                            </svg>-->
<!--                            {:else}-->
<!--                              <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">-->
<!--                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />-->
<!--                              </svg>-->
<!--                            {/if}-->
<!--                            Adjust stake-->
<!--                          </button>-->
<!--                        </div>-->
                      {/if}
                    </div>

                    <div class="sm:flex sm:items-center justify-between mt-6">
                      {#if $challengeDataUpdown.submissionHash || $challengeDataUpdown.phase === 1}
                        <div class="tracking-tight text-2xl text-center font-semibold text-white sm:text-3xl">
                          My prediction
                        </div>
                      {:else}
                        <div class="tracking-tight text-md text-left text-white font-bold sm:text-lg">
                          No prediction was submitted. Please wait for the next challenge.
                        </div>
                      {/if}
                      <div class="hidden sm:block sm:flex-grow"></div>
                      {#if $challengeDataUpdown.submissionHash}
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_"
                             class:sm:mr-4={$challengeDataUpdown.phase === 1}
                             class:sm:w-44={!$challengeDataUpdown.submissionHash || $challengeDataUpdown.phase > 1}>
                          <button on:click={downloadOwnSubmission} type="button"
                                  class="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-md bg-gray-100 hover:bg-gray-200 text-blue-700 hover:text-blue-800 focus:outline-none"> <!-- focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 -->
                            <!-- Heroicon name: solid/mail -->
                            <svg class="-ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd" d="M2 9.5A3.5 3.5 0 005.5 13H9v2.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 15.586V13h2.5a4.5 4.5 0 10-.616-8.958 4.002 4.002 0 10-7.753 1.977A3.5 3.5 0 002 9.5zm9 3.5H9V8a1 1 0 012 0v5z" clip-rule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </div>
                        {:else}
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_"
                             class:sm:mr-4={$challengeDataUpdown.phase === 1}
                             class:sm:w-44={!$challengeDataUpdown.submissionHash || $challengeDataUpdown.phase > 1}>
                          <button type="button" class:cursor-default={true} disabled={true}
                                  class="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-md bg-gray-400 text-gray-100"> <!-- focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 -->
                            No Submission
                          </button>
                        </div>
                      {/if}
                      {#if $challengeDataUpdown.phase === 1 }
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">
                          <button type="button"
                                  on:click={() => !(uploadingPrediction || updatingStake) && showUploadPopup(true)}
                                  class:bg-blue-500={true}
                                  class:hover:bg-blue-600={!(uploadingPrediction || updatingStake) && $challengeDataUpdown.stake >= $competitionDataUpdown.stakeThreshold}
                                  class:cursor-default={uploadingPrediction || updatingStake}
                                  class="w-full flex items-center justify-center px-4 py-3 text-white text-base font-medium rounded-md focus:outline-none">
                            {#if $challengeDataUpdown.submissionHash}
                              {#if uploadingPrediction}
                                <svg class="-ml-1 mr-3 animate-spin h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                     viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
                                  <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
                                </svg>
                              {:else}
                                <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
                                </svg>
                              {/if}
                              Update
                            {:else}
                              {#if uploadingPrediction || approvingForCompetition }
                                <svg class="-ml-1 mr-3 animate-spin h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                     viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
                                <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
                              </svg>
                              {:else}
                                <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
                                </svg>
                              {/if}
                              {approvingForCompetition ? `Approving`: `Submit`}
                            {/if}
                          </button>
                        </div>
                      {/if}
                    </div>
                    <div class="w-full text-gray-300 text-base font-medium h-10">
                      {#if ($challengeDataUpdown.stake < $competitionDataUpdown.stakeThreshold) && $challengeDataUpdown.phase === 1}
                        Please stake at least {$competitionDataUpdown.stakeThreshold} YIEDL to submit your prediction
                      {/if}
                    </div>

                    <div class="w-full text-white text-base sm:text-lg font-medium">
                      {#if $nextDeadlineUpdown}
                        {#if $challengeDataUpdown.phase === 1}
                          <p>Submissions will close on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {:else if $challengeDataUpdown.phase <= 3}
                          <p>Submissions closed. Challenge will close on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {:else}
                          <p>Next challenge will start on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {/if}
                        {#if $deadlineCountdownUpdown}
                        <span class="text-yellow-600 text-base sm:text-lg font-bold">
                          {#if $deadlineCountdownUpdown[0]}
                            <span class="tabular-nums">{$deadlineCountdownUpdown[0]}</span><span class="text-sm">d</span>
                          {/if}
                          {#if $deadlineCountdownUpdown[1]}
                            <span class="tabular-nums">{('' + $deadlineCountdownUpdown[1]).padStart(2, '0')}</span><span class="text-sm">h</span>
                          {/if}
                          <span class="tabular-nums">{('' + $deadlineCountdownUpdown[2]).padStart(2, '0')}</span><span class="text-sm">m</span>
                          <span class="tabular-nums">{('' + $deadlineCountdownUpdown[3]).padStart(2, '0')}</span><span class="text-sm">s</span>
                          left
                        </span>
                        {:else}
                          Hold on...
                        {/if}
                      {/if}
                    </div>

                  </div>
                </div>

              </section>

              <TablesAndCharts activeUser={$challengeDataUpdown.activeUser} leaderboard={$leaderboardUpdown} scoresAndRewards={$scoresAndRewardsUpdown}/>

            </div>
          </div>
        </div>
      {/if}
      {#if $selection === COMPS.NEUTRAL}
        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-5xl__ lg:px-8__">
          <h1 class="sr-only">Competition</h1>

          <!-- Main 3 column grid -->
          <div class="grid grid-cols-1 gap-4_ items-start lg:grid-cols-3_ lg:gap-8_">

            <!-- Left column -->
            <div class="grid grid-cols-1 gap-4 lg:col-span-2_">
              <!-- Welcome panel -->
              <section aria-labelledby="profile-overview-title">
                <div class="rounded-md bg-white overflow-hidden shadow">
                  <h2 class="sr-only" id="profile-overview-title">Competition Overview</h2>
                  <div class="bg-white p-6">
                    <p class="py-2 text-2xl font-extrabold sm:text-3xl text-center text-purple-900">{contracts.getCompetitionName($selection)} Competition</p>
                  </div>
                  <dl class="grid grid-cols-1 bg-white overflow-hidden border-t border-gray-200 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-y-0 sm:divide-x sm:divide-transparent">
                    <div class="px-3 py-2 sm:py-4">
                      <dt class="text-base font-normal text-center text-gray-900">
                        Total submissions
                      </dt>
                      <dd class="mt-1 justify-between text-2xl font-semibold text-indigo-600 text-center">
                        {$competitionDataNeutral.submissionCounter}
                      </dd>
                    </div>
                    <div class="px-3 py-2 sm:py-4">
                      <dt class="text-base font-normal text-center text-gray-900">
                        Total stake
                      </dt>
                      <dd class="mt-1 justify-between text-2xl font-semibold text-indigo-600 text-center">
                        {$competitionDataNeutral.totalStake}
                        <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>

              <section aria-labelledby="profile-overview-title">
                <div class="rounded-md bg-blue-800 overflow-hidden shadow">
                  <h2 class="sr-only" id="profile-overview-title">Competition Overview</h2>
                  <!-- <div class="border-b border-indigo-300 bg-indigo-700 grid grid-cols-1 divide-y divide-pink-300 sm:divide-y-0 sm:divide-x">
                    <Steps phase={$challengeData.phase} stakeLocked={!!$challengeData.submissionHash}></Steps>
                  </div> -->
                  <div class="p-8">

                    <div class="sm:flex sm:items-center justify-center">
                      <div class="text-2xl text-center font-semibold text-white sm:text-3xl">
                        <span class="tracking-tight">Challenge:</span> {$challengeDataNeutral.challenge}
                      </div>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700 mx-0 px-0">

                    <div class="sm:flex sm:items-center justify-between mt-6">
                      <div class="tracking-tight text-2xl text-center font-semibold text-white sm:text-3xl">
                        My stake: <span class="tracking-normal">{$challengeDataNeutral.stake.toFixed(2)}</span>
                        <span class="text-xl">{'__TOKEN_SYMBOL__'}</span>
                      </div>

                      {#if $challengeDataNeutral.phase === 1}
                        <!--                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">-->
                        <!--                          <button on:click={() => !(updatingStake || uploadingPrediction) && showStakingPopup(true)}-->
                        <!--                                  class:cursor-default={updatingStake || uploadingPrediction}-->
                        <!--                                  class:hover:bg-blue-600={!updatingStake || uploadingPrediction}-->
                        <!--                                  class="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-500 focus:outline-none">-->
                        <!--                            {#if updatingStake}-->
                        <!--                              <svg class="animate-spin -ml-1 mr-3 h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"-->
                        <!--                                   viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">-->
                        <!--                              <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />-->
                        <!--                            </svg>-->
                        <!--                            {:else}-->
                        <!--                              <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">-->
                        <!--                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />-->
                        <!--                              </svg>-->
                        <!--                            {/if}-->
                        <!--                            Adjust stake-->
                        <!--                          </button>-->
                        <!--                        </div>-->
                      {/if}
                    </div>

                    <div class="sm:flex sm:items-center justify-between mt-6">
                      {#if $challengeDataNeutral.submissionHash || $challengeDataNeutral.phase === 1}
                        <div class="tracking-tight text-2xl text-center font-semibold text-white sm:text-3xl">
                          My prediction
                        </div>
                      {:else}
                        <div class="tracking-tight text-md text-left text-white font-bold sm:text-lg">
                          No prediction was submitted. Please wait for the next challenge.
                        </div>
                      {/if}
                      <div class="hidden sm:block sm:flex-grow"></div>
                      {#if $challengeDataNeutral.submissionHash}
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_"
                             class:sm:mr-4={$challengeDataNeutral.phase === 1}
                             class:sm:w-44={!$challengeDataNeutral.submissionHash || $challengeDataNeutral.phase > 1}>
                          <button on:click={downloadOwnSubmission} type="button"
                                  class="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-md bg-gray-100 hover:bg-gray-200 text-blue-700 hover:text-blue-800 focus:outline-none"> <!-- focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 -->
                            <!-- Heroicon name: solid/mail -->
                            <svg class="-ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd" d="M2 9.5A3.5 3.5 0 005.5 13H9v2.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 15.586V13h2.5a4.5 4.5 0 10-.616-8.958 4.002 4.002 0 10-7.753 1.977A3.5 3.5 0 002 9.5zm9 3.5H9V8a1 1 0 012 0v5z" clip-rule="evenodd" />
                            </svg>
                            Download
                          </button>
                        </div>
                      {:else}
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_"
                             class:sm:mr-4={$challengeDataNeutral.phase === 1}
                             class:sm:w-44={!$challengeDataNeutral.submissionHash || $challengeDataNeutral.phase > 1}>
                          <button type="button" class:cursor-default={true} disabled={true}
                                  class="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-md bg-gray-400 text-gray-100"> <!-- focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 -->
                            No Submission
                          </button>
                        </div>
                      {/if}
                      {#if $challengeDataNeutral.phase === 1 }
                        <div class="mt-3 rounded-md shadow sm:mt-0 sm:flex-shrink-0_ sm:w-44">
                          <button type="button"
                                  on:click={() => !(uploadingPrediction || updatingStake) && showUploadPopup(true)}
                                  class:bg-blue-500={true}
                                  class:hover:bg-blue-600={!(uploadingPrediction || updatingStake) && $challengeDataNeutral.stake >= $competitionDataNeutral.stakeThreshold}
                                  class:cursor-default={uploadingPrediction || updatingStake}
                                  class="w-full flex items-center justify-center px-4 py-3 text-white text-base font-medium rounded-md focus:outline-none">
                            {#if $challengeDataNeutral.submissionHash}
                              {#if uploadingPrediction}
                                <svg class="-ml-1 mr-3 animate-spin h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                     viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
                                  <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
                                </svg>
                              {:else}
                                <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
                                </svg>
                              {/if}
                              Update
                            {:else}
                              {#if uploadingPrediction || approvingForCompetition }
                                <svg class="-ml-1 mr-3 animate-spin h-5 w-5" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                     viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve" stroke="text-white" fill="text-white" aria-hidden="true">
                                <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z" />
                              </svg>
                              {:else}
                                <svg xmlns="http://www.w3.org/2000/svg" class="-ml-1 mr-3 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
                                </svg>
                              {/if}
                              Submit
                            {/if}
                          </button>
                        </div>
                      {/if}
                    </div>
                    <div class="w-full text-gray-300 text-base font-medium h-10">
                      {#if ($challengeDataNeutral.stake < $competitionDataNeutral.stakeThreshold) && $challengeDataNeutral.phase === 1}
                        Please stake at least {$competitionDataNeutral.stakeThreshold} YIEDL to submit your prediction
                      {/if}
                    </div>

                    <div class="w-full text-white text-base sm:text-lg font-medium">
                      {#if $nextDeadlineUpdown}
                        {#if $challengeDataNeutral.phase === 1}
                          <p>Submissions will close on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {:else if $challengeDataNeutral.phase <= 3}
                          <p>Submissions closed. Challenge will close on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {:else}
                          <p>Next challenge will start on {TimeUtil.format($nextDeadlineUpdown)}</p>
                        {/if}
                        {#if $deadlineCountdownUpdown}
                        <span class="text-yellow-600 text-base sm:text-lg font-bold">
                          {#if $deadlineCountdownUpdown[0]}
                            <span class="tabular-nums">{$deadlineCountdownUpdown[0]}</span><span class="text-sm">d</span>
                          {/if}
                          {#if $deadlineCountdownUpdown[1]}
                            <span class="tabular-nums">{('' + $deadlineCountdownUpdown[1]).padStart(2, '0')}</span><span class="text-sm">h</span>
                          {/if}
                          <span class="tabular-nums">{('' + $deadlineCountdownUpdown[2]).padStart(2, '0')}</span><span class="text-sm">m</span>
                          <span class="tabular-nums">{('' + $deadlineCountdownUpdown[3]).padStart(2, '0')}</span><span class="text-sm">s</span>
                          left
                        </span>
                        {:else}
                          Hold on...
                        {/if}
                      {/if}
                    </div>

                  </div>
                </div>

              </section>

              <TablesAndCharts activeUser={$challengeDataNeutral.activeUser} leaderboard={$leaderboardNeutral} scoresAndRewards={$scoresAndRewardsNeutral}/>

            </div>
          </div>


        </div>
      {/if}

    </main>





    <footer>
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-5xl__ lg:px-8__ text-gray-600">
        <div class="h-20 relative flex flex-wrap items-center justify-between">
          <span class="block sm:inline">&copy; 2023 RCI</span> <span class="block sm:inline">All rights reserved</span>
        </div>
      </div>
    </footer>
  </div>

  <Notifications bind:this={notifications} />
{/if}
